import { ReactNode } from 'react';

import { styled } from '@mui/material';
import sumBy from 'lodash/sumBy';

import { RatioProgressBarSize } from './types';
import { useThemeColor } from '../../../hooks';
import { tokens } from '../../../theme/generated/tokens';
import { SxProps, WithRef } from '../../../types';
import { makeShouldForwardProp } from '../../../utils/shouldForwardProp';

type BarProps = {
  value: number;
  color?: string;
  sx?: SxProps;
  children?: ReactNode;
  testId?: string;
};

export type RatioProgressBarProps = {
  bars: Array<BarProps | false>;
  sx?: SxProps;
  size?: RatioProgressBarSize;
  total?: number;
  hideBorders?: boolean;
  className?: string;
  testId?: string;
  /**
   * Position (0–100) at which to render a benchmark tick marker
   * overlapping the progress bar.
   */
  benchmark?: number;
};

const Root = styled('div', {
  name: 'DsRatioProgressBar',
  slot: 'Root',
  target: 'DsRatioProgressBar-Root',
  shouldForwardProp: makeShouldForwardProp('hideBorders'),
  overridesResolver: ({ size = 'lg', hideBorders = false }, styles) => {
    return [
      styles.root,
      styles[size || 'lg'],
      hideBorders ? styles.noBorders : undefined,
    ];
  },
})<{ size: RatioProgressBarSize; hideBorders?: boolean }>``;

const BarContainer = styled('div', {
  name: 'DsRatioProgressBar',
  slot: 'BarContainer',
  target: 'DsRatioProgressBar-BarContainer',
  overridesResolver: (_, styles) => {
    return [styles.barContainer];
  },
})``;

const Bar = styled('div', {
  name: 'DsRatioProgressBar',
  slot: 'Bar',
  target: 'DsRatioProgressBar-Bar',
  overridesResolver: (_, styles) => {
    return [styles.bar];
  },
})``;

const Benchmark = styled('div', {
  name: 'DsRatioProgressBar',
  slot: 'Benchmark',
  target: 'DsRatioProgressBar-Benchmark',
  overridesResolver: (_, styles) => {
    return [styles.benchmark];
  },
})``;

const getPercentage = (value: number, total: number) => {
  return (value * 100) / (total || 1);
};

const RatioProgressBarSegment = ({
  value,
  color,
  sx,
  children,
  testId,
}: {
  value: number;
  color?: string;
  sx?: SxProps;
  children?: ReactNode;
  testId?: string;
}) => {
  const resolvedColor = useThemeColor(
    color || tokens.theme.fg.structural.primary
  );

  return (
    <BarContainer>
      <Bar
        data-testid={testId}
        style={{ backgroundColor: resolvedColor }}
        sx={{
          flexBasis: value + '%',
          ...sx,
        }}
      >
        {children}
      </Bar>
    </BarContainer>
  );
};

export const RatioProgressBar = ({
  ref,
  sx,
  size = 'lg',
  bars,
  total,
  className,
  testId,
  hideBorders = false,
  benchmark,
}: WithRef<HTMLDivElement, RatioProgressBarProps>) => {
  bars = bars.filter(Boolean);

  let barsSum = total;
  const _bars = [];

  if (barsSum == null) {
    barsSum = sumBy(bars, 'value');
  }

  let current = 0;
  for (const { value, color, ...rest } of bars as BarProps[]) {
    current += getPercentage(value, barsSum);
    _bars.push({ value: current, color, ...rest });
  }
  _bars.reverse();

  const clampedBenchmark =
    benchmark != null ? Math.min(100, Math.max(0, benchmark)) : undefined;

  return (
    <Root
      data-testid={testId}
      sx={sx}
      size={size}
      className={className}
      hideBorders={hideBorders}
      ref={ref}
    >
      {_bars?.map((bar, index) => (
        <RatioProgressBarSegment
          key={index}
          value={bar.value}
          color={bar.color}
          sx={bar.sx}
          testId={bar.testId}
        >
          {bar.children}
        </RatioProgressBarSegment>
      ))}
      {clampedBenchmark != null && (
        <Benchmark
          sx={{ left: `${clampedBenchmark}%` }}
          aria-label="benchmark"
        />
      )}
    </Root>
  );
};

RatioProgressBar.displayName = 'RatioProgressBar';
